// W.Sign portal API client — accounts, documents, signing flows.
// Talks to api.sign.wsoft.tr (prod) or the Vite proxy (dev).

import { ApiError } from './api'

const API_BASE = import.meta.env.PROD ? 'https://api.sign.wsoft.tr' : ''

const TOKEN_KEY = 'wsign.portal.jwt'
const EMAIL_KEY = 'wsign.portal.email'

// ── Session storage ─────────────────────────────────────────────────────────

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function getEmail(): string | null {
  return localStorage.getItem(EMAIL_KEY)
}

export function setSession(accessToken: string, email: string) {
  localStorage.setItem(TOKEN_KEY, accessToken)
  localStorage.setItem(EMAIL_KEY, email)
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(EMAIL_KEY)
}

export function isAuthenticated(): boolean {
  return !!getToken()
}

// ── Types ───────────────────────────────────────────────────────────────────

export interface DocumentSummary {
  id:               string
  title:            string
  originalFileName: string | null
  sourceKind:       string
  status:           string
  sizeBytes:        number
  createdAt:        string
  signatureCount:   number
}

export interface SignatureRequestSummary {
  id:                 string
  kind:               string
  recipientEmail:     string | null
  status:             string
  publicToken:        string | null
  createdAt:          string
  expiresAt:          string
  completedAt:        string | null
  /** True when a completed signature artifact (.imz/.p7s) is downloadable for this request. */
  hasSignedArtifact:  boolean
}

export interface DocumentDetail {
  id:                string
  title:             string
  originalFileName:  string | null
  sourceKind:        string
  status:            string
  sizeBytes:         number
  createdAt:         string
  contentType:       string
  /** How the SPA should display the document: "pdf" | "text" | "download". */
  viewMode:          'pdf' | 'text' | 'download'
  signatureRequests: SignatureRequestSummary[]
}

export interface SignatureSummary {
  documentId:         string
  signatureRequestId: string
  documentTitle:      string
  kind:               string
  recipientEmail:     string | null
  signerSubject:      string
  format:             string
  signedAt:           string
}

export interface InviteResponse {
  signatureRequestId: string
  publicToken:        string
  signUrl:            string
  expiresAt:          string
  /** False when the invite email could not be delivered; the signUrl can be shared manually. */
  emailDelivered?:    boolean
}

export interface RecipientSessionInfo {
  documentTitle: string
  status:        string
  signable:      boolean
  expiresAt:     string
  /** How the recipient SPA should display the document: "pdf" | "text" | "download". */
  viewMode:      'pdf' | 'text' | 'download'
}

export interface SignPrepareResponse {
  sessionId:   string
  dataToSign:  string
  ttlSeconds:  number
  /** True when the produced signature will be timestamped (-T level or higher). */
  timestamped: boolean
  /** True when the produced signature will be CAdES-ESXLong (long-term). Implies `timestamped`. */
  longTerm:    boolean
}

export interface SignCompleteResponse {
  status:           string
  signerSubject:    string
  signerThumbprint: string
  /** "CAdES-BES" or "CAdES-T". */
  format:           string
}

/** Server signing capabilities — used to decide the auto-selected signature level. */
export interface SignCapabilities {
  timestampAvailable: boolean
  /** True when the server can produce CAdES-ESXLong (long-term) signatures (non-PDF). */
  longTermAvailable:  boolean
  /** True when a PDF can be produced at PAdES-B-LT (long-term). Drives the PDF signing banner. */
  pdfLongTermAvailable: boolean
}

// ── Low-level request helpers ─────────────────────────────────────────────────

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const token = getToken()
  return token ? { ...extra, Authorization: `Bearer ${token}` } : extra
}

async function jsonOrThrow<T>(resp: Response): Promise<T> {
  if (!resp.ok) {
    const body = (await resp.json().catch(() => null)) as { error?: string; message?: string } | null
    if (resp.status === 401) clearSession()
    throw new ApiError(resp.status, body?.error ?? 'HTTP_ERROR', body?.message ?? resp.statusText)
  }
  return resp.json() as Promise<T>
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export async function requestMagicLink(email: string): Promise<void> {
  const resp = await fetch(`${API_BASE}/v1/portal/auth/request-link`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ email }),
  })
  if (!resp.ok && resp.status !== 202) {
    throw new ApiError(resp.status, 'REQUEST_FAILED', 'Giriş bağlantısı gönderilemedi.')
  }
}

export async function consumeMagicLink(token: string): Promise<{ accessToken: string; email: string }> {
  const resp = await fetch(`${API_BASE}/v1/portal/auth/consume`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ token }),
  })
  const data = await jsonOrThrow<{ accessToken: string; expiresAt: string; email: string }>(resp)
  setSession(data.accessToken, data.email)
  return { accessToken: data.accessToken, email: data.email }
}

/** Email + password login. On success the JWT session is stored just like a magic-link consume. */
export async function loginWithPassword(email: string, password: string): Promise<{ accessToken: string; email: string }> {
  const resp = await fetch(`${API_BASE}/v1/portal/auth/login`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ email, password }),
  })
  const data = await jsonOrThrow<{ accessToken: string; expiresAt: string; email: string }>(resp)
  setSession(data.accessToken, data.email)
  return { accessToken: data.accessToken, email: data.email }
}

/** Set or change the authenticated user's password (≥ 8 chars). */
export async function setPassword(password: string): Promise<void> {
  const resp = await fetch(`${API_BASE}/v1/portal/auth/set-password`, {
    method:  'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body:    JSON.stringify({ password }),
  })
  if (!resp.ok) {
    const body = (await resp.json().catch(() => null)) as { error?: string; message?: string } | null
    if (resp.status === 401) clearSession()
    throw new ApiError(resp.status, body?.error ?? 'HTTP_ERROR', body?.message ?? resp.statusText)
  }
}

export interface MeResponse {
  email:       string
  hasPassword: boolean
}

/** Current session info, including whether a password has been set (label "set" vs "change"). */
export async function me(): Promise<MeResponse> {
  return jsonOrThrow(await fetch(`${API_BASE}/v1/portal/auth/me`, { headers: authHeaders() }))
}

// ── Documents ───────────────────────────────────────────────────────────────

export async function listDocuments(): Promise<DocumentSummary[]> {
  return jsonOrThrow(await fetch(`${API_BASE}/v1/portal/documents`, { headers: authHeaders() }))
}

export async function getDocument(id: string): Promise<DocumentDetail> {
  return jsonOrThrow(await fetch(`${API_BASE}/v1/portal/documents/${id}`, { headers: authHeaders() }))
}

/** Completed signatures across the user's own documents — "önceki imzalar". */
export async function listSignatures(): Promise<SignatureSummary[]> {
  return jsonOrThrow(await fetch(`${API_BASE}/v1/portal/documents/signatures`, { headers: authHeaders() }))
}

export async function composeDocument(title: string, text: string): Promise<DocumentSummary> {
  return jsonOrThrow(await fetch(`${API_BASE}/v1/portal/documents/compose`, {
    method:  'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body:    JSON.stringify({ title, text }),
  }))
}

export async function uploadDocument(file: File, title?: string): Promise<DocumentSummary> {
  const form = new FormData()
  form.append('file', file)
  if (title) form.append('title', title)
  return jsonOrThrow(await fetch(`${API_BASE}/v1/portal/documents/upload`, {
    method:  'POST',
    headers: authHeaders(),
    body:    form,
  }))
}

export async function inviteToSign(documentId: string, recipientEmail: string): Promise<InviteResponse> {
  return jsonOrThrow(await fetch(`${API_BASE}/v1/portal/documents/${documentId}/invite`, {
    method:  'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body:    JSON.stringify({ recipientEmail }),
  }))
}

/** Delete a signature request (owner; any status). The signed artifact, if any, is also removed. */
export async function deleteSignRequest(documentId: string, requestId: string): Promise<void> {
  const resp = await fetch(`${API_BASE}/v1/portal/documents/${documentId}/sign-requests/${requestId}`, {
    method:  'DELETE',
    headers: authHeaders(),
  })
  if (!resp.ok) {
    const body = (await resp.json().catch(() => null)) as { error?: string; message?: string } | null
    if (resp.status === 401) clearSession()
    throw new ApiError(resp.status, body?.error ?? 'DELETE_FAILED', body?.message ?? 'İmza talebi silinemedi.')
  }
}

/** Fetch the owner's document content (auth required) as an object URL for inline (PDF) preview. */
export async function fetchDocumentPdfUrl(id: string): Promise<string> {
  const resp = await fetch(`${API_BASE}/v1/portal/documents/${id}/content`, { headers: authHeaders() })
  if (!resp.ok) throw new ApiError(resp.status, 'PDF_ERROR', 'Belge görüntülenemedi.')
  return URL.createObjectURL(await resp.blob())
}

/** Fetch the owner's document content as text (for TXT/XML inline display). */
export async function fetchDocumentText(id: string): Promise<string> {
  const resp = await fetch(`${API_BASE}/v1/portal/documents/${id}/content`, { headers: authHeaders() })
  if (!resp.ok) throw new ApiError(resp.status, 'CONTENT_ERROR', 'Belge görüntülenemedi.')
  return resp.text()
}

/** Trigger a browser download of the document's original bytes (auth required). */
export async function downloadDocumentOriginal(id: string, fileName: string): Promise<void> {
  const resp = await fetch(`${API_BASE}/v1/portal/documents/${id}/content?download=true`, { headers: authHeaders() })
  if (!resp.ok) throw new ApiError(resp.status, 'CONTENT_ERROR', 'Belge indirilemedi.')
  triggerBlobDownload(await resp.blob(), fileName)
}

/**
 * Download the signed artifact. `format` selects the CAdES container the endpoint should build
 * ("imz" attached / "p7s" detached); for a PAdES (PDF) document the server ignores it and returns
 * the signed PDF regardless. `fileName` is the exact name the browser saves — the caller picks the
 * correct extension (.pdf for PAdES, .imz/.p7s for CAdES), since the server's Content-Disposition
 * name is not readable cross-origin.
 */
export async function downloadSignedArtifact(
  documentId: string, format: 'imz' | 'p7s', fileName: string, requestId?: string,
): Promise<void> {
  const q = new URLSearchParams({ format })
  if (requestId) q.set('requestId', requestId)
  const resp = await fetch(`${API_BASE}/v1/portal/documents/${documentId}/signed?${q.toString()}`, {
    headers: authHeaders(),
  })
  if (!resp.ok) {
    const body = (await resp.json().catch(() => null)) as { error?: string; message?: string } | null
    if (resp.status === 401) clearSession()
    throw new ApiError(resp.status, body?.error ?? 'SIGNED_ERROR', body?.message ?? 'İmzalı belge indirilemedi.')
  }
  triggerBlobDownload(await resp.blob(), fileName)
}

function triggerBlobDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// ── Self-sign (owner) ──────────────────────────────────────────────────────────

/** Whether this server has a TSA configured (CAdES-T available). Cached per session. */
export async function signCapabilities(): Promise<SignCapabilities> {
  return jsonOrThrow(await fetch(`${API_BASE}/v1/portal/sign-capabilities`, {
    headers: authHeaders(),
  }))
}

export async function selfSignPrepare(documentId: string, signerCertificateB64: string, timestamp = false, longTerm = false): Promise<SignPrepareResponse> {
  return jsonOrThrow(await fetch(`${API_BASE}/v1/portal/documents/${documentId}/sign/prepare`, {
    method:  'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body:    JSON.stringify({ signerCertificate: signerCertificateB64, timestamp, longTerm }),
  }))
}

export async function selfSignComplete(documentId: string, sessionId: string, rawSignatureB64: string): Promise<SignCompleteResponse> {
  return jsonOrThrow(await fetch(`${API_BASE}/v1/portal/documents/${documentId}/sign/complete`, {
    method:  'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body:    JSON.stringify({ sessionId, rawSignature: rawSignatureB64 }),
  }))
}

// ── Developer / API keys (entegratörler) ───────────────────────────────────────
//
// Self-serve geliştirici konsolu. JWT authed; oturum sahibi yalnızca kendi
// entegratörlerini görür/yönetir. apiKey + hmacSecret SADECE oluşturma ve
// rotate yanıtlarında bir kez döner — listede asla yer almaz.

/** Listede dönen entegratör — secret içermez. */
export interface Integrator {
  integratorId:        string
  name:                string
  keyPrefix:           string   // ör. "wsk_ab12" — anahtarın yalnız ön eki
  callbackDomainAllow: string[]
  redirectDomainAllow: string[]
  monthlyQuota:        number
  monthlyUsage:        number
  enabled:             boolean
  createdAt:           string
  // ── Zaman damgası (RFC 3161 TSA) — write-only parola asla geri gelmez ──────
  /** Entegratörün TSA kimlik bilgisi tanımlı mı (CAdES-T üretiliyor mu). */
  tsaConfigured:       boolean
  tsaUrl?:             string | null
  tsaUsername?:        string | null
  tsaPolicyOid?:       string | null
  tsaHashAlgorithm?:   string | null
}

/**
 * TSA (RFC 3161 zaman damgası) yapılandırma alanları — create/update gövdesinde
 * gönderilir. `tsaPassword` WRITE-ONLY: sunucuda şifreli saklanır, GET'te dönmez.
 * Hepsi boş/atlanmışsa entegratör imzaları damgasız (CAdES-B) üretilir.
 */
export interface TsaConfigInput {
  tsaUrl?:           string
  tsaUsername?:      string
  /** WRITE-ONLY. Update'te boş bırakılırsa mevcut parola korunur. */
  tsaPassword?:      string
  tsaPolicyOid?:     string
  tsaHashAlgorithm?: string
}

/**
 * Oluşturma yanıtı — Integrator alanlarına ek olarak `apiKey` + `hmacSecret`
 * taşır. Bu değerler yalnızca burada döner; kaybedilirse anahtar yenilenmeli.
 */
export interface IntegratorCreated extends Integrator {
  apiKey:     string
  hmacSecret: string
}

/** Anahtar yenileme yanıtı — yalnız yeni gizli değerler. */
export interface IntegratorRotated {
  apiKey:     string
  hmacSecret: string
  keyPrefix:  string
}

export async function listIntegrators(): Promise<Integrator[]> {
  return jsonOrThrow(await fetch(`${API_BASE}/v1/portal/integrators`, { headers: authHeaders() }))
}

export async function createIntegrator(input: {
  name:                string
  callbackDomainAllow: string[]
  redirectDomainAllow: string[]
} & TsaConfigInput): Promise<IntegratorCreated> {
  return jsonOrThrow(await fetch(`${API_BASE}/v1/portal/integrators`, {
    method:  'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body:    JSON.stringify(input),
  }))
}

export async function updateIntegrator(id: string, input: {
  callbackDomainAllow: string[]
  redirectDomainAllow: string[]
} & TsaConfigInput): Promise<Integrator> {
  return jsonOrThrow(await fetch(`${API_BASE}/v1/portal/integrators/${encodeURIComponent(id)}`, {
    method:  'PATCH',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body:    JSON.stringify(input),
  }))
}

export async function rotateIntegratorKey(id: string): Promise<IntegratorRotated> {
  return jsonOrThrow(await fetch(`${API_BASE}/v1/portal/integrators/${encodeURIComponent(id)}/rotate-key`, {
    method:  'POST',
    headers: authHeaders(),
  }))
}

export async function disableIntegrator(id: string): Promise<void> {
  const resp = await fetch(`${API_BASE}/v1/portal/integrators/${encodeURIComponent(id)}/disable`, {
    method:  'POST',
    headers: authHeaders(),
  })
  if (!resp.ok) {
    const body = (await resp.json().catch(() => null)) as { error?: string; message?: string } | null
    if (resp.status === 401) clearSession()
    throw new ApiError(resp.status, body?.error ?? 'DISABLE_FAILED', body?.message ?? 'Anahtar devre dışı bırakılamadı.')
  }
}

// ── Recipient (anonymous, via /s/{token}) ──────────────────────────────────────

export async function recipientInfo(token: string): Promise<RecipientSessionInfo> {
  return jsonOrThrow(await fetch(`${API_BASE}/v1/portal/sign-requests/${token}`))
}

export function recipientPdfUrl(token: string): string {
  return `${API_BASE}/v1/portal/sign-requests/${token}/content`
}

/** Fetch the recipient document content as text (TXT/XML inline display). Anonymous. */
export async function recipientText(token: string): Promise<string> {
  const resp = await fetch(`${API_BASE}/v1/portal/sign-requests/${token}/content`)
  if (!resp.ok) throw new ApiError(resp.status, 'CONTENT_ERROR', 'Belge görüntülenemedi.')
  return resp.text()
}

/** Trigger a browser download of the recipient document's original bytes. Anonymous. */
export async function recipientDownload(token: string, fileName: string): Promise<void> {
  const resp = await fetch(`${API_BASE}/v1/portal/sign-requests/${token}/content?download=true`)
  if (!resp.ok) throw new ApiError(resp.status, 'CONTENT_ERROR', 'Belge indirilemedi.')
  triggerBlobDownload(await resp.blob(), fileName)
}

export async function recipientPrepare(token: string, signerCertificateB64: string, timestamp = false, longTerm = false): Promise<SignPrepareResponse> {
  return jsonOrThrow(await fetch(`${API_BASE}/v1/portal/sign-requests/${token}/prepare`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ signerCertificate: signerCertificateB64, timestamp, longTerm }),
  }))
}

export async function recipientComplete(token: string, sessionId: string, rawSignatureB64: string): Promise<SignCompleteResponse> {
  return jsonOrThrow(await fetch(`${API_BASE}/v1/portal/sign-requests/${token}/complete`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ sessionId, rawSignature: rawSignatureB64 }),
  }))
}
